import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface GenerateModalProps {
  markdown: string;
  template: string;
  cardCount: number;
  /** True while the LLM is still streaming. Disables copy/download/save
   *  (so the user doesn't act on a half-written doc) and shows a
   *  "streaming…" tag. */
  streaming?: boolean;
  /** Called when user clicks "Save to project" with the user's chosen
   *  title. When provided, the Save button + title input appear post-stream
   *  alongside Copy/Download. Resolution (success / failure) is the
   *  caller's responsibility — modal stays open until `onClose` fires. */
  onSave?: (title: string) => Promise<void>;
  onClose: () => void;
}

type View = 'rendered' | 'raw';

/** Sensible default title — first H1 in the markdown, falling back to
 *  `<template> · <date>` if nothing parseable yet (mid-stream / empty). */
function defaultTitle(template: string, markdown: string): string {
  for (const line of markdown.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.startsWith('# ')) {
      return trimmed.slice(2).trim().slice(0, 80);
    }
    if (trimmed.length > 0 && !trimmed.startsWith('#')) break;
  }
  return `${template} · ${new Date().toLocaleString()}`;
}

export function GenerateModal({ markdown, template, cardCount, streaming = false, onSave, onClose }: GenerateModalProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState<View>('rendered');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Title field is initialized once (not synced with markdown) — first
  // touch wins. Stays empty until stream finishes; we default-fill on
  // first focus / first save attempt to avoid jitter while text streams in.
  const [title, setTitle] = useState('');

  const ensureTitle = (): string => {
    const t = title.trim();
    if (t) return t;
    const fallback = defaultTitle(template, markdown);
    setTitle(fallback);
    return fallback;
  };

  const handleSave = async () => {
    if (!onSave || saving) return;
    const finalTitle = ensureTitle();
    setSaving(true);
    try {
      await onSave(finalTitle);
      setSaved(true);
    } catch (e) {
      console.error('save failed', e);
      alert(t('generate.saveFailed', { message: (e as Error).message }));
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.warn('clipboard failed', e);
    }
  };

  const download = () => {
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(26, 24, 20, 0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 8,
          width: '90%',
          maxWidth: 920,
          height: '88%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--rule)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span className="serif" style={{ fontSize: 18, fontWeight: 500 }}>
            {t('generate.heading', { template })}
          </span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '.05em' }}>
            {t('generate.fromCards', { count: cardCount })}
          </span>
          {streaming && (
            <span
              className="mono"
              style={{
                fontSize: 10.5,
                padding: '3px 8px',
                borderRadius: 999,
                background: 'var(--blue-soft, #eaf2ff)',
                color: 'var(--blue, #2b6cb0)',
                fontWeight: 600,
                letterSpacing: '.04em',
              }}
              aria-live="polite"
            >
              ▍ {t('generate.streaming')}
            </span>
          )}
          <span style={{ flex: 1 }} />

          {/* View toggle */}
          <div
            style={{
              display: 'inline-flex',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              overflow: 'hidden',
              marginRight: 4,
            }}
          >
            {(['rendered', 'raw'] as View[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className="mono"
                style={{
                  fontSize: 10.5,
                  padding: '6px 12px',
                  background: view === v ? 'var(--ink-0)' : 'white',
                  color: view === v ? 'white' : 'var(--ink-2)',
                  fontWeight: 600,
                  letterSpacing: '.04em',
                }}
              >
                {v === 'rendered' ? t('generate.viewRendered') : t('generate.viewRaw')}
              </button>
            ))}
          </div>

          <button
            onClick={copy}
            disabled={streaming}
            className="mono"
            style={{
              fontSize: 11, padding: '6px 12px',
              border: '1px solid var(--rule)', borderRadius: 4,
              background: copied ? 'var(--blue-soft)' : 'white',
              color: copied ? 'var(--blue)' : 'var(--ink-1)',
              fontWeight: 600,
              opacity: streaming ? 0.45 : 1,
              cursor: streaming ? 'not-allowed' : 'pointer',
            }}
            title={streaming ? t('generate.waitStreaming') : t('generate.copyTitle')}
          >
            {copied ? t('generate.copied') : t('generate.copyMarkdown')}
          </button>
          <button
            onClick={download}
            disabled={streaming}
            className="mono"
            style={{
              fontSize: 11, padding: '6px 12px',
              border: '1px solid var(--rule)', borderRadius: 4,
              background: 'white', color: 'var(--ink-1)', fontWeight: 600,
              opacity: streaming ? 0.45 : 1,
              cursor: streaming ? 'not-allowed' : 'pointer',
            }}
            title={streaming ? t('generate.waitStreaming') : t('generate.downloadTitle')}
          >
            {t('generate.downloadMd')}
          </button>
          {onSave && !streaming && (
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onFocus={() => { if (!title) setTitle(defaultTitle(template, markdown)); }}
              placeholder={t('generate.titlePlaceholder')}
              disabled={saving || saved}
              style={{
                fontSize: 12,
                padding: '5px 9px',
                width: 220,
                border: '1px solid var(--rule)',
                borderRadius: 4,
                background: saved ? 'var(--paper-1)' : 'white',
                color: 'var(--ink-1)',
                outline: 'none',
              }}
            />
          )}
          {onSave && (
            <button
              onClick={handleSave}
              disabled={streaming || saving || saved}
              className="mono"
              style={{
                fontSize: 11, padding: '6px 12px',
                border: '1px solid var(--ink-0)', borderRadius: 4,
                background: saved ? 'var(--blue-soft)' : 'var(--ink-0)',
                color: saved ? 'var(--blue)' : 'white',
                fontWeight: 700, letterSpacing: '.04em',
                opacity: streaming ? 0.45 : 1,
                cursor: streaming || saving ? 'not-allowed' : 'pointer',
              }}
              title={
                streaming ? t('generate.waitStreaming') :
                saved ? t('generate.savedTitle') :
                t('generate.saveTitle')
              }
            >
              {saved ? t('generate.saved') : saving ? t('generate.saving') : t('common.save')}
            </button>
          )}
          <button
            onClick={onClose}
            className="mono"
            style={{
              fontSize: 12, color: 'var(--ink-3)',
              padding: '4px 10px', border: '1px solid var(--rule)', borderRadius: 4,
            }}
            title={t('common.close')}
          >
            ✕
          </button>
        </div>

        {/* Body — rendered (default) or raw */}
        {view === 'rendered' ? (
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px 40px 40px',
              background: 'var(--paper-0)',
            }}
          >
            <article className="md-render">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
            </article>
          </div>
        ) : (
          <pre
            style={{
              flex: 1,
              margin: 0,
              padding: '20px 32px',
              overflowY: 'auto',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 12.5,
              lineHeight: 1.65,
              color: 'var(--ink-1)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: 'var(--paper-0)',
            }}
          >
            {markdown}
          </pre>
        )}
      </div>
    </div>
  );
}
